import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { UserContext } from '../../../contexts';
import { MASection } from '../MASection';
import { IChat } from './IChat';

// ── Owner Admin: chat participants ───────────────────────────────────────

export function ChatMembersSection() {
  const { user, activeStoreId } = useContext(UserContext);
  const storeId = activeStoreId ?? user?.store_id_m ?? null;

  const [state, setState] = useState({ loading: true });
  const [selection, setSelection] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!storeId) {
      setState({ loading: false, error: 'Pick a store first — the Master Store has no chat group of its own.' });
      return;
    }
    let cancelled = false;
    setState({ loading: true });
    axios.get('/api/merchant-chat/members', { params: { store_id: storeId }, metadata: { silent: true } })
      .then(({ data }) => {
        if (cancelled) return;
        setState({ loading: false, data });
        setSelection((data?.member_ids ?? []).map(Number));
      })
      .catch((e) => {
        if (cancelled) return;
        setState({ loading: false, error: e.response?.data?.message || e.message });
      });
    return () => { cancelled = true; };
  }, [storeId]);

  const toggleUser = (id) => {
    setNotice('');
    setSelection((current) => (
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    ));
  };

  const save = async () => {
    setSaving(true);
    setNotice('');
    try {
      const { data } = await axios.put('/api/merchant-chat/members', {
        store_id: storeId,
        user_ids: selection,
      });
      setSelection((data?.member_ids ?? []).map(Number));
      setNotice('Saved');
    } catch (e) {
      setNotice(e.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const body = () => {
    if (state.loading) return <p className="ma-section__empty">Loading…</p>;
    if (state.error) return <p className="ma-section__empty">{state.error}</p>;

    const { data } = state;
    const lockedId = Number(data?.locked_user_id) || null;

    return (
      <>
        {(data?.candidates ?? []).map((candidate) => {
          const id = Number(candidate.id);
          const locked = lockedId === id;
          return (
            <label key={id} className="ma-chat-member">
              <input
                type="checkbox"
                checked={locked || selection.includes(id)}
                disabled={locked}
                onChange={() => toggleUser(id)}
              />
              <span>{candidate.name}</span>
              <span className="ma-chat-member__meta">
                {candidate.email} · {candidate.role === 'owner' ? 'Owner' : 'Sales admin'}
                {locked ? ' · always included' : ''}
              </span>
            </label>
          );
        })}
        {(data?.candidates ?? []).length === 0 && (
          <p className="ma-section__empty">No owners or sales admins on this store yet — grant roles in Store Admin first.</p>
        )}
        <div className="ma-filters">
          <button type="button" className="rc-btn rc-btn--small" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Save participants'}
          </button>
          {notice && <span className="ma-section__count">{notice}</span>}
        </div>
      </>
    );
  };

  return (
    <MASection title="Chat with Retail Circle" icon={<IChat />}>
      <p className="ma-section__hint">
        Who from your team is in your store&apos;s chat group with the Retail Circle team
        (<strong>{state.data?.channel?.name || 'your store channel'}</strong>). Pick from your
        owners and sales admins; the store owner is always included.
      </p>
      {body()}
    </MASection>
  );
}
